import React, { useRef, useState, useMemo, useEffect } from "react";

import { twJoin } from "tailwind-merge";
import SupPlusCircle from "../../../icons/PlusCircle";
import { toPixels } from "../../../utils/css";
import { BaseButton } from "./components/BaseButton";

import { RemoveButton } from "./components/RemoveButton";
import SupDepositAlt from "../../../icons/DepositAlt";

export interface DepositButtonProps {
  fluid?: boolean;
  completed?: boolean;
  disabled?: boolean;
  defaultButton?: "primary" | "secondary";
  onDeposit?: () => void;
  onBuyNow?: () => void;
  onRemove?: () => void;
}

const CONTAINER_PADDING_RIGHT = 40;
const EMPTY_BUTTON_WIDTH = 48;

export const SupDepositButton = ({ fluid = false, completed = false, disabled = false, defaultButton = "primary", onDeposit, onBuyNow, onRemove }: DepositButtonProps) => {
  const [buttonWidth, setButtonWidth] = useState<number>();
  const [primaryHovered, setPrimaryHovered] = useState(false);
  const [secondaryHovered, setSecondaryHovered] = useState(false);
  const [removeButtonHovered, setRemoveButtonHovered] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const primaryBtnTextRef = useRef<HTMLSpanElement>(null);
  const secondaryBtnTextRef = useRef<HTMLSpanElement>(null);

  const containerWidth = useMemo(() => (buttonWidth ? buttonWidth + CONTAINER_PADDING_RIGHT : undefined), [buttonWidth]);
  const containerStyles = useMemo(
    () =>
      fluid
        ? { minWidth: toPixels(containerWidth) }
        : {
            width: toPixels(containerWidth),
            paddingRight: toPixels(CONTAINER_PADDING_RIGHT),
          },
    [fluid, containerWidth]
  );

  useEffect(() => {
    const primaryContentWidth = primaryBtnTextRef.current?.offsetWidth ?? 0;
    const secondaryContentWidth = secondaryBtnTextRef.current?.offsetWidth ?? 0;
    const roundedContentWidth = Math.ceil(Math.max(primaryContentWidth, secondaryContentWidth) / 2) * 2;

    setButtonWidth(roundedContentWidth + EMPTY_BUTTON_WIDTH);
  }, [fluid]);

  const createMouseEnterHandler = (buttonType: "primary" | "secondary") => () => {
    if (buttonType === "primary") {
      setPrimaryHovered(true);
    } else {
      setSecondaryHovered(true);
    }
  };

  const createMouseLeaveHandler = (buttonType: "primary" | "secondary") => () => {
    if (buttonType === "primary") {
      setPrimaryHovered(false);
    } else {
      setSecondaryHovered(false);
    }
  };

  return (
    <div ref={containerRef} className={twJoin("deposit-button relative h-10 overflow-hidden border-neutral-600", fluid && "w-full")} style={containerStyles}>
      <div className={twJoin("absolute h-20 w-full will-change-transform transition-transform duration-300", completed && !disabled && "-translate-y-10")}>
        <div className="absolute left-0 top-0 right-10 bottom-0 h-10">
          <BaseButton
            disabled={disabled}
            textCenter={fluid && ((!secondaryHovered && defaultButton === "primary") || (primaryHovered && defaultButton === "secondary"))}
            className="absolute left-0 w-full"
            icon={<SupDepositAlt width={24} height={24} />}
            onMouseEnter={createMouseEnterHandler("primary")}
            onMouseLeave={createMouseLeaveHandler("primary")}
            onClick={onDeposit}
          >
            <span ref={primaryBtnTextRef}>Deposit</span>
          </BaseButton>

          <BaseButton
            disabled={disabled}
            textCenter={fluid && ((secondaryHovered && defaultButton === "primary") || (!primaryHovered && defaultButton === "secondary"))}
            className={twJoin(
              "absolute left-full w-full",
              ((defaultButton === "primary" && !secondaryHovered) || (defaultButton === "secondary" && primaryHovered)) && "-translate-x-0",
              ((defaultButton === "primary" && secondaryHovered) || (defaultButton === "secondary" && !primaryHovered)) && "-translate-x-[calc(100%-40px)]"
            )}
            icon={<SupPlusCircle width={24} height={24} />}
            secondary
            onMouseEnter={createMouseEnterHandler("secondary")}
            onMouseLeave={createMouseLeaveHandler("secondary")}
            onClick={onBuyNow}
          >
            <span ref={secondaryBtnTextRef}>To bag</span>
          </BaseButton>
        </div>

        <RemoveButton
          hovered={primaryHovered || secondaryHovered || removeButtonHovered}
          textCenter
          disabled={disabled}
          className={twJoin("absolute w-full left-0 bottom-0 right-0", completed ? "pointer-events-auto" : "pointer-events-none")}
          onClick={onRemove}
          onMouseLeave={() => {
            setRemoveButtonHovered(false);
          }}
          onMouseEnter={() => {
            setRemoveButtonHovered(true);
          }}
        />

        <span
          className={twJoin(
            "absolute block h-10 border-r right-0 top-0 transition-colors duration-300",
            !secondaryHovered && !disabled && "border-neutral-600",
            secondaryHovered && !disabled && "border-neutral-700",
            disabled && defaultButton === "primary" && "border-neutral-100",
            disabled && defaultButton === "secondary" && "border-transparent"
          )}
        />
      </div>
    </div>
  );
};
